import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getPricingPackagesByGameType, PricingPackage } from '../../config/pricing';
import { GameConfig } from '../../config';
import { BLITZ_GAMES, TEAM_GAMES, FAMILY_GAMES, PARTY_GAMES } from '../../config/games';
import { AppIcon, type AppIconName } from '../shared/icons';
import { useTranslation } from '../../i18n';
import {
  getGameLinkName,
  getPricingPackageDescription,
  getPricingPackageName,
  translateGames,
} from '../../i18n/helpers';
import './PremiumPage.css';

interface PremiumPageProps {
  gameType: 'blitz' | 'team' | 'family' | 'party';
}

const PremiumPage: React.FC<PremiumPageProps> = ({ gameType }) => {
  const { t, i18n } = useTranslation();
  const [packages, setPackages] = useState<PricingPackage[]>([]);
  const [loadingPackageId, setLoadingPackageId] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderOffset, setSliderOffset] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const visibleTiles = 3;

  useEffect(() => {
    const gamePackages = getPricingPackagesByGameType(gameType);
    setPackages(gamePackages);
  }, [gameType]);

  useEffect(() => {
    const calculateOffset = () => {
      if (containerRef.current && sliderRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const gap = 24;
        const tileWidth = (containerWidth - (visibleTiles - 1) * gap) / visibleTiles;
        const offset = currentSlide * (tileWidth + gap);
        setSliderOffset(-offset);
      }
    };

    calculateOffset();
    window.addEventListener('resize', calculateOffset);
    return () => window.removeEventListener('resize', calculateOffset);
  }, [currentSlide, visibleTiles]);

  const handleCheckout = async (packageId: string) => {
    setLoadingPackageId(packageId);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      console.log('Creating checkout session for package:', packageId);
      console.log('Backend URL:', backendUrl);

      const response = await fetch(`${backendUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          packageId,
          successUrl: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/pricing?canceled=true`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Backend error response:', errorData);
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Checkout session created:', data);

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned from server');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      const errorMessage = error instanceof Error ? error.message : t('menu.premium.checkoutRetry');
      alert(t('menu.premium.checkoutFailed', { message: errorMessage }));
    } finally {
      setLoadingPackageId(null);
    }
  };

  const getGamesForType = (): GameConfig[] => {
    switch (gameType) {
      case 'blitz': return BLITZ_GAMES;
      case 'team': return TEAM_GAMES;
      case 'family': return FAMILY_GAMES;
      case 'party': return PARTY_GAMES;
      default: return [];
    }
  };

  const games = useMemo(() => translateGames(getGamesForType()), [gameType, i18n.language]);
  const gameTypeName = getGameLinkName(gameType);

  const allGamesBenefit = {
    icon: 'gamepad' as AppIconName,
    title: t('menu.premium.allGamesTitle', { gameType: gameTypeName }),
    description: t('menu.premium.allGamesDescription', { gameType: gameTypeName }),
    games: games,
  };

  // Order: customize space, company questions, all games, reserved/scores/stats, custom room names last
  // Spec: docs/ROOM_BRANDING_SPEC.md §5.1
  const benefits = [
    {
      icon: 'palette' as AppIconName,
      title: t('menu.premium.benefits.customizeSpace.title'),
      description: t('menu.premium.benefits.customizeSpace.description'),
    },
    {
      icon: 'lightbulb' as AppIconName,
      title: t('menu.premium.benefits.companyQuestions.title'),
      description: t('menu.premium.benefits.companyQuestions.description'),
    },
    allGamesBenefit,
    {
      icon: 'lock' as AppIconName,
      title: t('menu.premium.benefits.reservedRoom.title'),
      description: t('menu.premium.benefits.reservedRoom.description'),
    },
    {
      icon: 'barChart' as AppIconName,
      title: t('menu.premium.benefits.keepingScores.title'),
      description: t('menu.premium.benefits.keepingScores.description'),
    },
    {
      icon: 'trendingUp' as AppIconName,
      title: t('menu.premium.benefits.statistics.title'),
      description: t('menu.premium.benefits.statistics.description'),
    },
    {
      icon: 'tag' as AppIconName,
      title: t('menu.premium.benefits.customRoomNames.title'),
      description: t('menu.premium.benefits.customRoomNames.description'),
    },
  ];

  const allBenefits = benefits;
  const totalSlides = Math.max(1, allBenefits.length - visibleTiles + 1);

  const goToSlide = (index: number) => {
    if (index >= 0 && index < totalSlides) {
      setCurrentSlide(index);
    }
  };

  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      goToSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      goToSlide(currentSlide - 1);
    }
  };

  return (
    <div className="premium-page">
      <h2 className="premium-page__title">
        {t('menu.premium.choosePlan')}
      </h2>

      <p className="premium-page__subtitle">
        {t('menu.premium.choosePlanSubtitle')}
      </p>

      <div className="premium-page__packages">
        {packages.map((pkg) => (
          <div key={pkg.id} className="premium-page__card">
            <div>
              <h4 className="premium-page__package-name">
                {getPricingPackageName(pkg.id)}
              </h4>
              <p className="premium-page__package-desc">
                {getPricingPackageDescription(pkg.id)}
              </p>
              <div className="premium-page__price">
                {pkg.price}€
              </div>
              <div className="premium-page__duration">
                {t('menu.premium.durationAccess', { duration: pkg.duration })}
              </div>
            </div>
            <button
              className="premium-page__cta"
              onClick={() => handleCheckout(pkg.id)}
              disabled={loadingPackageId !== null}
            >
              {loadingPackageId === pkg.id ? t('common.processing') : t('common.getStarted')}
            </button>
          </div>
        ))}
      </div>

      <h3 className="premium-page__benefits-title">
        <AppIcon name="premium" sx={{ fontSize: '1.25em' }} />
        {t('menu.premium.benefitsTitle')}
      </h3>

      <p className="premium-page__benefits-subtitle">
        {t('menu.premium.benefitsSubtitle')}
      </p>

      <div className="premium-page__slider-wrap">
        <div ref={containerRef} className="premium-page__slider-viewport">
          <div
            ref={sliderRef}
            className="premium-page__slider-track"
            style={{ transform: `translateX(${sliderOffset}px)` }}
          >
            {allBenefits.map((benefit, index) => (
              <div
                key={index}
                className="premium-page__benefit-card"
                style={{
                  flex: `0 0 calc((100% - ${(visibleTiles - 1) * 1.5}rem) / ${visibleTiles})`,
                }}
              >
                <div className="premium-page__benefit-icon">
                  <AppIcon name={benefit.icon} sx={{ fontSize: '2.25rem', color: 'var(--color-primary)' }} />
                </div>
                <h3 className="premium-page__benefit-title">
                  {benefit.title}
                </h3>
                <p className="premium-page__benefit-desc">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <button
          className="premium-page__nav-btn premium-page__nav-btn--prev"
          onClick={prevSlide}
          disabled={currentSlide === 0}
          type="button"
        >
          ‹
        </button>
        <button
          className="premium-page__nav-btn premium-page__nav-btn--next"
          onClick={nextSlide}
          disabled={currentSlide >= totalSlides - 1}
          type="button"
        >
          ›
        </button>

        <div className="premium-page__dots">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              type="button"
              className={`premium-page__dot${currentSlide === index ? ' premium-page__dot--active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={t('common.goToSlide', { index: index + 1 })}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PremiumPage;
